// ===== BEGIN FILE: src/utils/permanentDataCache.ts =====
/**
 * Permanent Data Cache System
 * 
 * Caches birth chart data that NEVER changes for a given birth profile.
 * Uses localStorage with userId-birthProfileId composite keys.
 */

export type CacheScope = 'permanent' | 'halfday' | 'daily' | 'weekly' | 'monthly';

interface CacheEntry<T> {
  data: T;
  timestamp: number;
  scope: CacheScope;
  version: string;
}

const CACHE_VERSION = 'v1';

// Expiry times by scope
const EXPIRY_MS = {
  permanent: 365 * 24 * 60 * 60 * 1000, // 1 year (effectively permanent)
  halfday: 12 * 60 * 60 * 1000, // 12 hours (twice per day)
  daily: 24 * 60 * 60 * 1000,
  weekly: 7 * 24 * 60 * 60 * 1000,
  monthly: 30 * 24 * 60 * 60 * 1000
};

/**
 * Generate cache key with user and birth profile context
 */
export function getCacheKey(
  userId: string,
  birthProfileId: string,
  dataType: string
): string {
  return `chart_${userId}_${birthProfileId}_${dataType}_${CACHE_VERSION}`;
}

/**
 * Get cached data if valid
 */
export function getCachedData<T>(
  userId: string,
  birthProfileId: string,
  dataType: string
): T | null {
  try {
    const key = getCacheKey(userId, birthProfileId, dataType);
    const item = localStorage.getItem(key);
    if (!item) return null;

    const entry: CacheEntry<T> = JSON.parse(item);
    
    // Check version
    if (entry.version !== CACHE_VERSION) {
      localStorage.removeItem(key);
      return null;
    }

    // Check expiry
    const expiryMs = EXPIRY_MS[entry.scope];
    if (Date.now() - entry.timestamp > expiryMs) {
      localStorage.removeItem(key);
      return null;
    }

    return entry.data;
  } catch {
    return null;
  }
}

/**
 * Save data to cache with scope
 */
export function setCachedData<T>(
  userId: string,
  birthProfileId: string,
  dataType: string,
  data: T,
  scope: CacheScope = 'permanent'
): void {
  try {
    const key = getCacheKey(userId, birthProfileId, dataType);
    const entry: CacheEntry<T> = {
      data,
      timestamp: Date.now(),
      scope,
      version: CACHE_VERSION
    };
    localStorage.setItem(key, JSON.stringify(entry));
  } catch (e) {
    console.warn('Cache save failed (quota exceeded?):', e);
  }
}

/**
 * Clear cache for a specific birth profile (when profile deleted/updated)
 */
export function clearBirthProfileCache(userId: string, birthProfileId: string): void {
  try {
    const prefix = `chart_${userId}_${birthProfileId}_`;
    const keys = Object.keys(localStorage);
    keys.forEach(key => {
      if (key.startsWith(prefix)) {
        localStorage.removeItem(key);
      }
    });
  } catch {}
}

/**
 * Clear all chart caches for user (on logout)
 */
export function clearUserCache(userId: string): void {
  try {
    const prefix = `chart_${userId}_`;
    const keys = Object.keys(localStorage);
    keys.forEach(key => {
      if (key.startsWith(prefix)) {
        localStorage.removeItem(key);
      }
    });
  } catch {}
}

/**
 * Get current dasha period string for cache key
 * Format: MD-AD-PD (e.g., "Venus-Sun-Moon")
 */
export function getCurrentDashaPeriodKey(
  md: string,
  ad: string,
  pd: string
): string {
  return `${md}-${ad}-${pd}`;
}

/**
 * Get date-based cache key for daily refreshing data
 */
export function getDailyKey(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
}

// ===== END FILE: src/utils/permanentDataCache.ts =====
